/**
 * Alpine.js Block & Delete Modal Component
 *
 * Two-step flow:
 *   1. Confirm — block + delete the thread immediately on click.
 *   2. Reason  — after the block succeeds, optionally pick a category
 *                / leave a note that gets filed as a moderator report
 *                via /v1/report.
 *
 * Triggered by window.openBlockDeleteModal({ threadId, contactUid, contactUsername }).
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

export function blockDeleteModal() {
    return {
        isOpen: false,
        step: 'confirm',
        threadId: '',
        contactUid: '',
        contactUsername: '',
        reasonSel: '',
        reason: '',
        isSubmitting: false,
        reasonOptions: [],
        titles: { confirm: '', reason: '' },

        init() {
            const optsEl = this.$el.querySelector('script[data-block-reason-options]');
            try {
                this.reasonOptions = optsEl ? JSON.parse(optsEl.textContent) : [];
            } catch (e) {
                this.reasonOptions = [];
            }
            const titlesEl = this.$el.querySelector('script[data-block-titles]');
            try {
                this.titles = titlesEl ? JSON.parse(titlesEl.textContent) : this.titles;
            } catch (e) { /* keep defaults */ }
            window.openBlockDeleteModal = (opts) => this.open(opts || {});
        },

        open({ threadId, contactUid, contactUsername }) {
            this.threadId = String(threadId || '');
            this.contactUid = String(contactUid || '');
            this.contactUsername = String(contactUsername || '');
            this.reasonSel = '';
            this.reason = '';
            this.step = 'confirm';
            this.isSubmitting = false;
            this.isOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                if (window.LucideIcons) window.LucideIcons.initialize(this.$el);
            });
        },

        close() {
            if (this.isSubmitting) return;
            this.isOpen = false;
            unlockBodyScroll();
        },

        toggleReason(value) {
            if (value === '__no_problem') {
                this.close();
                return;
            }
            this.reasonSel = this.reasonSel === value ? '' : value;
        },

        async confirmBlock() {
            if (this.isSubmitting || !this.threadId) return;
            this.isSubmitting = true;

            const t = window.t || ((k) => k);
            const errMsg = t('common.pm.block_error');

            try {
                const data = await new Promise((resolve, reject) => {
                    if (typeof window.jQuery === 'undefined') {
                        reject(new Error('jQuery unavailable'));
                        return;
                    }
                    window.jQuery.ajax({
                        type: 'POST',
                        url: '/app/messages/delete-thread',
                        data: {
                            thread: this.threadId,
                            block_user: 1,
                        },
                        dataType: 'json',
                    }).done((data) => {
                        // Auth/CSRF/onboarding gate returns {redirect: url} for a
                        // lapsed session. The global ajaxComplete handler
                        // navigates, so do not resolve into the block-failed path.
                        if (data && typeof data.redirect === 'string' && data.redirect) {
                            return;
                        }
                        resolve(data);
                    }).fail(() => reject(new Error(errMsg)));
                });

                if (!data || !data.success) {
                    throw new Error((data && data.error) || errMsg);
                }

                if (typeof window.removeThreadRow === 'function') {
                    // Routes through the shared removal so the in-flight-poll guard
                    // is set, the pane closes only when this thread is open, and the
                    // Message Requests UI (#3039) is notified to drop its row.
                    window.removeThreadRow(this.threadId);
                } else if (window.jQuery) {
                    window.jQuery('#thread_' + this.threadId).remove();
                    if (window.pm_view_mode === 'chat') {
                        window.jQuery('#threads_main').removeClass('threads--open');
                        window.jQuery('#threads_right').empty();
                    }
                }

                this.step = 'reason';
                this.$nextTick(() => {
                    if (window.LucideIcons) window.LucideIcons.initialize(this.$el);
                });
            } catch (error) {
                if (window.showError) {
                    window.showError(error.message || errMsg);
                } else {
                    alert(error.message || errMsg);
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        async sendReport() {
            if (this.isSubmitting || !this.reasonSel || !this.contactUid) return;
            this.isSubmitting = true;

            const t = window.t || ((k) => k);

            try {
                const token = window.getApiToken
                    ? await window.getApiToken()
                    : (window.getAuthToken ? window.getAuthToken() : null);
                if (!token) throw new Error(t('common.auth_required'));

                const body = new URLSearchParams({
                    entity_type: 'user',
                    target_id: this.contactUid,
                    target_uid: this.contactUid,
                    reason_sel: this.reasonSel,
                });
                const note = this.reason && this.reason.trim();
                if (note) body.set('reason', '[reported while blocking] ' + note);
                else body.set('reason', '[reported while blocking]');

                const response = await fetch('/v1/report', {
                    method: 'POST',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    credentials: 'include',
                    body,
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));

                    // Already reported inside the re-report cooldown. The block
                    // itself already went through, so close out normally rather
                    // than reporting a failure the user cannot act on.
                    if (data && data.error && data.error.code === 'report_duplicate') {
                        if (window.showSuccess) {
                            window.showSuccess(data.error.message || t('legacy.report.recent_duplicate'));
                        }
                        this.isOpen = false;
                        unlockBodyScroll();
                        return;
                    }

                    throw new Error((data && data.error && data.error.message) || t('legacy.report.submit_failed'));
                }

                if (window.showSuccess) {
                    window.showSuccess(t('legacy.report.submitted'));
                }
                this.isOpen = false;
                unlockBodyScroll();
            } catch (error) {
                if (window.showError) {
                    window.showError(error.message || t('legacy.report.submit_failed'));
                } else {
                    alert(error.message || t('legacy.report.submit_failed'));
                }
            } finally {
                this.isSubmitting = false;
            }
        },
    };
}

if (typeof window !== 'undefined') {
    window.blockDeleteModal = blockDeleteModal;
}

export default blockDeleteModal;
